/**
 * Active-alarm store — the per-site list of active alarm groups of the site
 * entity, shown on the alarms summary card and history blade.
 *
 * One entry per loaded site, keyed by site id: `{ list, generatedAt }`. The
 * `alarms` socket event full-replaces the list; `generatedAt` is the monotonic
 * guard timestamp kept as a sibling field so an optimistic single-alarm
 * removal can't reset it and let an out-of-order emit resurrect a cleared
 * alarm. Clearing an alarm optimistically drops the row before the
 * authoritative socket replace arrives.
 *
 * Contains:
 * - siteAlarms slice (replaceAlarms, removeAlarm) + reducer (default export)
 */
import { createSlice } from '@reduxjs/toolkit';

const slice = createSlice({
  name: 'siteAlarms',
  initialState: { bySiteId: {} },
  reducers: {
    /** Full-replace a site's active-alarm list and its monotonic guard timestamp. */
    replaceAlarms(state, action) {
      const { siteId, list, generatedAt } = action.payload;
      state.bySiteId[String(siteId)] = { list: list ?? [], generatedAt: generatedAt ?? null };
    },
    /** Optimistically drop one alarm row ahead of the authoritative socket replace. */
    removeAlarm(state, action) {
      const { siteId, alarmStateId } = action.payload;
      const entry = state.bySiteId[String(siteId)];
      if (!entry) return;
      entry.list = entry.list.filter(alarm => alarm.id !== alarmStateId);
    },
  },
});

export const { replaceAlarms, removeAlarm } = slice.actions;
export default slice.reducer;
